import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { BRAND } from '@/brand';
import '../styles/serviceSelect.css';

const LOGO_URL = 'https://storage.googleapis.com/connct-cba18.firebasestorage.app/frontend-assets/Conncct-logo.png';

type Service = 'accounting' | 'consulting';

const CheckIcon = () => (
  <svg viewBox="0 0 24 24"><path d="M20 6 9 17l-5-5"/></svg>
);

function Login() {
  const navigate = useNavigate();

  const [selected, setSelected] = useState<Service>(() => {
    const saved = localStorage.getItem('activeServiceView');
    return saved === 'accounting' ? 'accounting' : 'consulting';
  });

  // Each domain is a single product, so skip the chooser and go straight to the
  // brand's sign-in page (corporate → accounting; consulting → consulting).
  useEffect(() => {
    navigate(BRAND === 'corporate' ? '/accounting/login' : '/consulting/login', { replace: true });
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const handleContinue = () => {
    localStorage.setItem('activeServiceView', selected);
    if (selected === 'accounting') {
      navigate('/accounting/login');
    } else {
      navigate('/consulting/login');
    }
  };

  const pick = (val: Service) => setSelected(val);

  return (
    <div className="ss-page">
      <div className="ss-dots" aria-hidden="true" />

      <nav className="ss-nav">
        <a href="/" className="ss-logo">
          <img src={LOGO_URL} alt="Conncct" style={{ height: 32, width: 'auto' }} />
        </a>
        <div className="ss-nav-right">
          <a className="ss-login" href="/signup">Create account</a>
        </div>
      </nav>

      <div className="ss-wrap">
        <header className="ss-head">
          <span className="ss-eyebrow">Welcome back</span>
          <h1>Which service are you signing in to?</h1>
          <p>Select your workspace below. You'll be taken straight to your dashboard.</p>
        </header>

        <div className="ss-grid" role="radiogroup" aria-label="Choose a service to log in to">

          {/* Accounting & Tax — gold */}
          <div
            className={`ss-opt gold${selected === 'accounting' ? ' sel' : ''}`}
            role="radio"
            aria-checked={selected === 'accounting'}
            tabIndex={0}
            onClick={() => pick('accounting')}
            onKeyDown={e => { if (e.key === ' ' || e.key === 'Enter') { e.preventDefault(); pick('accounting'); } }}
          >
            <span className="ss-radio"><CheckIcon /></span>
            <div className="ss-tile">
              <svg viewBox="0 0 24 24">
                <rect x="3" y="7" width="18" height="13" rx="2.5"/>
                <path d="M3 7a2 2 0 0 1 2-2h12a2 2 0 0 1 2 2v0H5a2 2 0 0 0-2 2Z"/>
                <path d="M16 12.5h3.5v3H16a1.5 1.5 0 0 1 0-3Z"/>
              </svg>
            </div>
            <div className="ss-kicker">Bookkeeping · VAT · Tax</div>
            <h3>Accounting &amp; Tax</h3>
            <p className="ss-lede">Your books, VAT filings and UAE corporate tax — all in one place.</p>
            <ul className="ss-feats">
              <li><span className="ss-feat-ic"><CheckIcon /></span>Bookkeeping &amp; bank reconciliation</li>
              <li><span className="ss-feat-ic"><CheckIcon /></span>VAT returns &amp; corporate tax filing</li>
              <li><span className="ss-feat-ic"><CheckIcon /></span>FTA compliance calendar &amp; deadlines</li>
            </ul>
          </div>

          {/* Capital Readiness — blue */}
          <div
            className={`ss-opt${selected === 'consulting' ? ' sel' : ''}`}
            role="radio"
            aria-checked={selected === 'consulting'}
            tabIndex={0}
            onClick={() => pick('consulting')}
            onKeyDown={e => { if (e.key === ' ' || e.key === 'Enter') { e.preventDefault(); pick('consulting'); } }}
          >
            <span className="ss-radio"><CheckIcon /></span>
            <div className="ss-tile">
              <svg viewBox="0 0 24 24">
                <path d="M3 3v16a2 2 0 0 0 2 2h16"/>
                <path d="M7 14l4-4 3 3 5-6"/>
                <path d="M16 7h3v3"/>
              </svg>
            </div>
            <div className="ss-kicker">For raising</div>
            <h3>Capital Readiness</h3>
            <p className="ss-lede">Your readiness score, financial model and fundraising tools.</p>
            <ul className="ss-feats">
              <li><span className="ss-feat-ic"><CheckIcon /></span>Investor readiness score &amp; assessment</li>
              <li><span className="ss-feat-ic"><CheckIcon /></span>Financial model, pitch deck &amp; data room</li>
              <li><span className="ss-feat-ic"><CheckIcon /></span>Advisor sessions &amp; fundraising pipeline</li>
            </ul>
          </div>

        </div>

        <div className="ss-foot">
          <button className="ss-back" onClick={() => navigate(-1)}>← Back</button>
          <div className="ss-cont-wrap">
            <button className="ss-continue" onClick={handleContinue}>
              {selected === 'accounting'
                ? 'Sign in to Accounting →'
                : 'Sign in to Capital Readiness →'}
            </button>
          </div>
        </div>

        <p className="ss-legal">
          By continuing you agree to our{' '}
          <a href="/privacy">Terms &amp; Privacy Policy</a>.
        </p>
      </div>
    </div>
  );
}

export default Login;
